import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle, XCircle, Clock, AlertCircle, Store, ArrowLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useApp } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

const PaymentConfirmPage = () => {
  const { t } = useTranslation();
  const { paymentId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { api, haptic, refreshData } = useApp();

  const [payment, setPayment] = useState(location.state?.payment || null);
  const [status, setStatus] = useState(payment?.status || 'pending');
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [polling, setPolling] = useState(true);

  // Fetch payment if not in state
  useEffect(() => {
    if (!payment && paymentId) {
      fetchPayment();
    }
  }, [paymentId]);

  // Poll for status updates
  useEffect(() => {
    if (!polling || status !== 'pending') return;

    const interval = setInterval(async () => {
      try {
        const res = await api.get(`/payments/${paymentId}`);
        setPayment(res.data);
        setStatus(res.data.status);

        if (res.data.status !== 'pending') {
          setPolling(false);
          haptic(res.data.status === 'confirmed' ? 'success' : 'error');
          refreshData();
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 2000);

    return () => clearInterval(interval);
  }, [polling, status, paymentId]);

  // Countdown timer
  useEffect(() => {
    if (status !== 'pending') return;

    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          setStatus('expired');
          setPolling(false);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [status]);

  const fetchPayment = async () => {
    try {
      const res = await api.get(`/payments/${paymentId}`);
      setPayment(res.data);
      setStatus(res.data.status);

      // Calculate time left
      const expires = new Date(res.data.expires_at);
      const now = new Date();
      const diff = Math.max(0, Math.floor((expires - now) / 1000));
      setTimeLeft(diff);

      if (res.data.status !== 'pending') {
        setPolling(false);
      }
    } catch (err) {
      toast.error(t('confirm.not_found'));
      navigate('/');
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleDone = () => {
    haptic('light');
    navigate('/');
  };

  const handleDemoConfirm = async () => {
    haptic('medium');
    try {
      await api.post(`/demo/simulate-confirm/${payment.payment_code}`);
      toast.success(t('confirm.confirmed_toast'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('confirm.failed_toast'));
    }
  };

  if (!payment) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="spinner" />
      </div>
    );
  }

  const statusConfig = {
    pending: {
      icon: Clock,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-100 dark:bg-yellow-900/30',
      title: t('confirm.awaiting_title'),
      description: t('confirm.awaiting_desc')
    },
    confirmed: {
      icon: CheckCircle,
      color: 'text-green-500',
      bgColor: 'bg-green-100 dark:bg-green-900/30',
      title: t('confirm.success_title'),
      description: t('confirm.success_desc')
    },
    rejected: {
      icon: XCircle,
      color: 'text-red-500',
      bgColor: 'bg-red-100 dark:bg-red-900/30',
      title: t('confirm.rejected_title'),
      description: t('confirm.rejected_desc')
    },
    expired: {
      icon: AlertCircle,
      color: 'text-muted-foreground',
      bgColor: 'bg-gray-100 dark:bg-gray-900/30',
      title: t('confirm.expired_title'),
      description: t('confirm.expired_desc')
    }
  };

  const currentStatus = statusConfig[status] || statusConfig.pending;
  const StatusIcon = currentStatus.icon;

  return (
    <div className="min-h-screen bg-background safe-top safe-bottom" data-testid="payment-confirm-page">
      {/* Header */}
      <div className="px-4 py-4 flex items-center">
        <button
          onClick={() => navigate('/')}
          className="lp-press w-10 h-10 rounded-full flex items-center justify-center hover:bg-[var(--lp-tile)]"
          data-testid="back-home-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
      </div>

      <div className="px-4 py-6 space-y-6 animate-slide-up">
        {/* Status Card */}
        <div className="receipt p-6 space-y-6">
          {/* Status Icon */}
          <div className="flex flex-col items-center">
            <div className={cn(
              "w-20 h-20 rounded-full flex items-center justify-center mb-4",
              currentStatus.bgColor,
              status === 'confirmed' && 'success-icon'
            )}>
              <StatusIcon className={cn("w-10 h-10", currentStatus.color)} />
            </div>
            <h2 className="text-xl font-bold">{currentStatus.title}</h2>
            <p className="text-muted-foreground text-sm text-center">
              {currentStatus.description}
            </p>
          </div>

          {/* Timer (if pending) */}
          {status === 'pending' && (
            <div className="flex flex-col items-center">
              <span className="text-sm text-muted-foreground">{t('confirm.expires_in')}</span>
              <span className={cn(
                "countdown text-2xl font-bold",
                timeLeft < 30 && "urgent"
              )}>
                {formatTime(timeLeft)}
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-dashed border-[rgb(var(--lp-edge)/var(--lp-edge-a))]" />

          {/* Payment Details */}
          <div className="space-y-4">
            {/* Merchant */}
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[var(--lp-tile)] flex items-center justify-center">
                <Store className="w-6 h-6 text-muted-foreground" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t('confirm.merchant')}</p>
                <p className="font-semibold">{payment.merchant_name}</p>
              </div>
            </div>

            {/* Amount */}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('common.amount')}</span>
              <span className="font-mono text-2xl font-bold">
                {payment.amount} {payment.currency}
              </span>
            </div>

            {/* Payment Code */}
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">{t('confirm.payment_code')}</span>
              <span className="font-mono text-lg font-semibold text-link">
                {payment.payment_code}
              </span>
            </div>

            {/* Time */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{t('confirm.time')}</span>
              <span className="text-muted-foreground">
                {new Date(payment.created_at).toLocaleString()}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {status === 'pending' && (
            <Button
              onClick={handleDemoConfirm}
              className="w-full h-14 rounded-full bg-green-500 hover:bg-green-600"
              data-testid="demo-confirm-btn"
            >
              <CheckCircle className="w-5 h-5 mr-2" />
              {t('confirm.demo_confirm')}
            </Button>
          )}

          <Button
            onClick={handleDone}
            variant={status === 'pending' ? 'outline' : 'default'}
            className="w-full h-14 rounded-full"
            data-testid="done-btn"
          >
            {status === 'pending' ? t('confirm.cancel_home') : t('common.done')}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirmPage;
